import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../lib/api';
import {
  IdentificationCard, Users, ShieldCheck, Key, Cube,
  PaperPlaneTilt, CheckSquare, MagnifyingGlass, GearSix,
  SignOut, List, X, CaretDown, CaretRight, UserCircle,
  ClipboardText, ChartBar, Warning, Ticket, ArrowsLeftRight,
  House, Bell, Brain, Lightning
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

import ActionCenterBell from '../components/ActionCenterBell';
import ProductUpdatesBulb from '../components/ProductUpdatesBulb';
import GlobalSearch from '../components/GlobalSearch';

// Simplified navigation structure with role-based visibility
const navGroups = [
  {
    id: 'my-access',
    label: 'My Access',
    defaultOpen: true,
    items: [
      { path: '/', icon: IdentificationCard, label: 'My Identity' },
      { path: '/access-requests', icon: PaperPlaneTilt, label: 'Request Access', badgeKey: 'access-requests' },
      { path: '/jit-access', icon: Lightning, label: 'Privileged Access', badgeKey: 'jit-access' },
      { path: '/approvals', icon: CheckSquare, label: 'Approvals', badgeKey: 'approvals' },
    ]
  },
  {
    id: 'governance',
    label: 'Governance',
    defaultOpen: true,
    adminOnly: true,
    excludeRoles: ['report_viewer'],
    items: [
      { path: '/dashboard', icon: House, label: 'Dashboard' },
      { path: '/identities', icon: Users, label: 'Identities' },
      { path: '/roles', icon: ShieldCheck, label: 'Roles' },
      { path: '/entitlements', icon: Key, label: 'Entitlements' },
      { path: '/applications', icon: Cube, label: 'Applications' },
    ]
  },
  {
    id: 'compliance',
    label: 'Compliance',
    defaultOpen: false,
    adminOnly: true,
    items: [
      { path: '/certifications', icon: ClipboardText, label: 'Certifications', badgeKey: 'certifications', featureId: 'certifications' },
      { path: '/lifecycle', icon: ArrowsLeftRight, label: 'Lifecycle (JML)', featureId: 'lifecycle' },
      { path: '/sod', icon: Warning, label: 'SoD Policies', featureId: 'sod' },
      { path: '/role-mining', icon: Brain, label: 'Role Mining', featureId: 'role_mining' },
      { path: '/reports', icon: ChartBar, label: 'Reports', featureId: 'reports', allowRoles: ['admin', 'governance_admin', 'report_viewer'] },
    ]
  },
  {
    id: 'admin',
    label: 'Administration',
    defaultOpen: false,
    adminOnly: true,
    excludeRoles: ['report_viewer'],
    items: [
      { path: '/provisioning-tickets', icon: Ticket, label: 'Provisioning', badgeKey: 'provisioning-tickets' },
      { path: '/ai-search', icon: MagnifyingGlass, label: 'AI Search' },
      { path: '/admin', icon: GearSix, label: 'Settings' },
    ]
  },
];

// Collapsible nav section component
const NavSection = ({ group, isActive, sidebarOpen, setSidebarOpen, isOpen, onToggle, badgeCounts = {} }) => {
  const groupTotal = group.items.reduce((sum, item) => sum + (item.badgeKey ? (badgeCounts[item.badgeKey] || 0) : 0), 0);
  return (
    <div className="mb-2">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-widest hover:text-slate-300 transition-colors"
      >
        <span className="flex items-center gap-2">
          {group.label}
          {!isOpen && groupTotal > 0 && (
            <span className="min-w-[16px] h-4 flex items-center justify-center rounded-full bg-orange-500 text-white text-[9px] font-bold px-1">
              {groupTotal > 99 ? '99+' : groupTotal}
            </span>
          )}
        </span>
        <CaretRight 
          size={12} 
          weight="bold" 
          className={`transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} 
        />
      </button>
      
      <div className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
        {group.items.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          const count = item.badgeKey ? (badgeCounts[item.badgeKey] || 0) : 0;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => setSidebarOpen(false)}
              className={`group flex items-center gap-3 px-3 py-2 mx-1 rounded-md text-sm transition-all duration-150 ${
                active
                  ? 'bg-orange-500/10 text-orange-400 border-l-2 border-orange-500'
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
              }`}
            >
              <Icon size={18} weight={active ? 'fill' : 'regular'} />
              <span className="font-medium flex-1">{item.label}</span>
              {count > 0 && (
                <span
                  data-testid={`sidebar-badge-${item.badgeKey}`}
                  className="min-w-[20px] h-5 flex items-center justify-center rounded-full bg-orange-500/20 text-orange-400 text-[10px] font-bold px-1.5"
                >
                  {count > 99 ? '99+' : count}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [badgeCounts, setBadgeCounts] = useState({});
  const [featureToggles, setFeatureToggles] = useState({});
  
  // Track which sections are open
  const [openSections, setOpenSections] = useState(() => {
    const initial = {};
    navGroups.forEach(g => {
      initial[g.id] = g.defaultOpen;
    });
    return initial;
  });

  // Fetch sidebar badge counts
  const loadBadgeCounts = useCallback(async () => {
    try {
      const res = await API.get('/sidebar-counts');
      setBadgeCounts(res.data);
    } catch {
      // silent
    }
  }, []);

  // Load feature toggles
  const loadFeatures = useCallback(async () => {
    try {
      const res = await API.get('/features');
      setFeatureToggles(res.data.features || {});
    } catch {
      // silent — default all enabled
    }
  }, []);

  // Load on mount, on route change, and poll every 60s
  useEffect(() => {
    loadBadgeCounts();
    loadFeatures();
    const interval = setInterval(loadBadgeCounts, 60000);
    return () => clearInterval(interval);
  }, [loadBadgeCounts, loadFeatures]);

  useEffect(() => {
    loadBadgeCounts();
  }, [location.pathname, loadBadgeCounts]);

  // Auto-expand section containing current route
  useEffect(() => {
    navGroups.forEach(group => {
      if (group.items.some(item => {
        if (item.path === '/') return location.pathname === '/';
        return location.pathname.startsWith(item.path);
      })) {
        setOpenSections(prev => ({ ...prev, [group.id]: true }));
      }
    });
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const toggleSection = (id) => {
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Check if user is admin (has any admin role)
  const isAdmin = user?.admin_role && user.admin_role !== 'viewer';
  const userRole = user?.admin_role || 'viewer';

  // Filter nav items based on role + feature toggles
  const getVisibleGroups = () => {
    return navGroups
      .filter(group => {
        if (group.adminOnly && !isAdmin) return false;
        if (group.excludeRoles && group.excludeRoles.includes(userRole)) return false;
        return true;
      })
      .map(group => ({
        ...group,
        items: group.items.filter(item => {
          // Feature toggle check
          if (item.featureId) {
            const feature = featureToggles[item.featureId];
            if (feature && feature.enabled === false) return false;
          }
          // Role-specific allowlist
          if (item.allowRoles && !item.allowRoles.includes(userRole)) return false;
          return true;
        })
      }))
      .filter(group => group.items.length > 0);
  };

  const visibleGroups = getVisibleGroups();

  return (
    <div className="flex h-screen overflow-hidden bg-slate-100">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Cleaner, more minimal */}
      <aside
        data-testid="main-sidebar"
        className={`fixed lg:static inset-y-0 left-0 z-50 w-60 bg-slate-900 text-slate-300 flex flex-col transform transition-transform duration-200 shrink-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Logo - Simplified */}
        <div className="flex items-center gap-3 px-4 h-14 border-b border-slate-800">
          <div className="w-8 h-8 rounded-md bg-orange-500 flex items-center justify-center">
            <ShieldCheck size={18} className="text-white" weight="bold" />
          </div>
          <span className="text-sm font-bold text-white tracking-tight">IAG Platform</span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {visibleGroups.map((group) => (
              <NavSection
                key={group.id}
                group={group}
                isActive={isActive}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                isOpen={openSections[group.id]}
                onToggle={() => toggleSection(group.id)}
                badgeCounts={badgeCounts}
              />
          ))}
        </nav>

        {/* User section - Cleaner */}
        <div className="border-t border-slate-800 p-3">
          <div className="flex items-center gap-3 px-2 py-2 rounded-md bg-slate-800/50">
            <div className="w-8 h-8 rounded-md bg-slate-700 flex items-center justify-center text-xs font-bold text-white">
              {user?.name?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-white truncate">{user?.name || 'Admin'}</p>
              <p className="text-[10px] text-slate-500 truncate capitalize">{user?.admin_role || 'viewer'}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar - Simplified */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 shrink-0 shadow-sm">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-2 hover:bg-slate-100 rounded-md transition-colors"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
            </button>
            <button
              data-testid="global-search-trigger"
              onClick={() => window.dispatchEvent(new Event('open-global-search'))}
              className="hidden sm:flex items-center gap-2 px-3 h-8 rounded-md border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors text-sm text-navy-400"
            >
              <MagnifyingGlass size={14} />
              <span className="text-xs">Search...</span>
              <kbd className="ml-2 px-1.5 py-0.5 rounded border border-slate-200 bg-white text-[10px] font-mono text-navy-400">⌘K</kbd>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <ProductUpdatesBulb />
            <ActionCenterBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 text-sm hover:bg-slate-100 rounded-md h-9" data-testid="user-menu-trigger">
                  <div className="w-6 h-6 rounded-md bg-slate-800 flex items-center justify-center text-[10px] font-bold text-white">
                    {user?.name?.charAt(0) || 'A'}
                  </div>
                  <span className="hidden sm:inline text-navy-700 font-medium">{user?.name || 'Admin'}</span>
                  <CaretDown size={12} weight="bold" className="text-navy-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 rounded-md shadow-lg">
                <div className="px-3 py-2 border-b border-slate-100">
                  <p className="text-xs font-medium text-navy-900">{user?.name || 'Admin'}</p>
                  <p className="text-[10px] text-navy-500">{user?.email || 'admin@iag.local'}</p>
                </div>
                <DropdownMenuItem
                  onClick={() => navigate('/notification-preferences')}
                  className="cursor-pointer text-sm py-2"
                  data-testid="notification-preferences-link"
                >
                  <Bell size={14} className="mr-2 text-navy-500" weight="regular" />
                  Notifications
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-600 cursor-pointer text-sm py-2"
                  data-testid="logout-button"
                >
                  <SignOut size={14} className="mr-2" weight="bold" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <div key={location.pathname} className="animate-page-enter min-w-0">
            {children}
          </div>
        </main>
      </div>
      <GlobalSearch />
    </div>
  );
}
